import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, message } from "antd";
import TherapiesTable from "./TherapiesTable";
import TherapyForm from "./TherapyForm";
import {
  getTherapiesApi,
  deleteTherapyApi,
  uploadTherapiesApi,
} from "../../../services/therapy.services";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import { PlusOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { UiDataTable } from "../../ui";
import BulkUploadModal from "../../common/BulkUploadModal";
import { prepareTherapyBulkUploadFile } from "../../../helpers/bulkUploadExcel";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";

const Therapies = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const page = parsePageParam(params);
  const searchQuery = parseSearchParam(params) || "";
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const defaultRole = userDetails?.currRole || getCookie(Role);
  const isOrgAdmin =
    defaultRole !== "Facility_Admin" && defaultRole !== "Receptionist";

  const setPage = (newPage) => {
    updateUrlSearchParams(navigate, { page: newPage });
  };

  const fetchData = (extraParams = {}) => {
    setLoading(true);
    getTherapiesApi({ page, search: searchQuery, ...extraParams })
      .then((res) => {
        setData(res?.data?.results || []);
        setTotal(res?.data?.count || 0);
      })
      .catch(() => message.error("Failed to load therapies"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [page, searchQuery]);

  const deleteTherapy = (id) => {
    deleteTherapyApi(id)
      .then(() => {
        message.success("Therapy Deleted");
        fetchData();
      })
      .catch(() => message.error("Delete Failed"));
  };

  const onSearch = (e) => {
    updateUrlSearchParams(navigate, {
      search: e?.target?.value || null,
      page: 1,
    });
  };

  return (
    <>
      <UiDataTable
        showHeader={false}
        shellClassName="ui-table-fill-shell"
        onSearch={onSearch}
        searchValue={searchQuery}
        searchPlaceholder="Search therapy"
        toolbarTrailing={
          <div style={{ display: "flex", gap: 12 }}>
            {isOrgAdmin && (
              <Button
                icon={<CloudUploadOutlined />}
                onClick={() => setUploadModalOpen(true)}
              >
                Upload Excel
              </Button>
            )}
            <Button
              type="primary"
              onClick={() => setOpen(true)}
              icon={<PlusOutlined />}
            >
              Add Therapy
            </Button>
          </div>
        }
      >
        <TherapiesTable
          data={data}
          loading={loading}
          refresh={fetchData}
          deleteTherapy={deleteTherapy}
          page={page}
          setPage={setPage}
          total={total}
        />
      </UiDataTable>

      {open && (
        <TherapyForm open={open} setOpen={setOpen} refresh={fetchData} />
      )}

      {uploadModalOpen && (
        <BulkUploadModal
          open={uploadModalOpen}
          setOpen={setUploadModalOpen}
          refreshPage={fetchData}
          title="Bulk Upload Therapies"
          requiredColumns={["Name", "Description"]}
          sampleFileName="bulk_import_curengo_therapy_sample.xlsx"
          uploadApi={uploadTherapiesApi}
          fileField="therapies"
          prepareUploadFile={prepareTherapyBulkUploadFile}
        />
      )}
    </>
  );
};

export default Therapies;
